import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import "./InlineSurveyDropdowns.css";
import { showToast } from "../../../../../../core/common-service/CommonService";
import { useQuestionsContext } from "../context/QuestionsContext";

const InlineSurveyDropdowns = ({
  onSubmit,
  hasGeneratedContent,
}) => {
  const { survey_questions, onSurveyAnswerClick, loading } = useQuestionsContext();
  const { t } = useTranslation();

  // Only track temporary input text (not the selection state)
  const [customInputs, setCustomInputs] = useState({});
  const [showErrors, setShowErrors] = useState(false);

  // Get short label based on question index
  const getShortLabel = (questionIndex) => {
    if (questionIndex === 0) return t("style");
    if (questionIndex === 1) return t("profile");
    return `Question ${questionIndex + 1}`;
  };

  // Handle predefined answer selection
  const handleAnswerSelect =
    (questionIndex, answerIndex, isMobile = false) =>
    (e) => {
      if (showErrors) {
        setShowErrors(false);
      }

      // Clear custom input if switching from custom to predefined
      if (customInputs[questionIndex]) {
        setCustomInputs((prev) => {
          const updated = { ...prev };
          delete updated[questionIndex];
          return updated;
        });
      }

      // Just call the parent's handler (isCustom = false)
      onSurveyAnswerClick(questionIndex, answerIndex, false)(e);

      // Manually close the dropdown after selection
      const dropdownId = isMobile
        ? `dropdownMenuButtonMobile${questionIndex}`
        : `dropdownMenuButton${questionIndex}`;
      const dropdownElement = document.getElementById(dropdownId);
      if (dropdownElement) {
        const bsDropdown =
          window.bootstrap?.Dropdown?.getInstance(dropdownElement);
        if (bsDropdown) {
          bsDropdown.hide();
        }
      }
    };

  // Handle custom input text change (only updates local state)
  const handleCustomInputChange = (questionIndex) => (e) => {
    const value = e.target.value;
    setCustomInputs((prev) => ({
      ...prev,
      [questionIndex]: value,
    }));

    if (showErrors && value.trim()) {
      setShowErrors(false);
    }
  };

  // Handle submitting custom answer
  const handleSubmitCustomAnswer =
    (questionIndex, isMobile = false) =>
    (e) => {
      e.preventDefault();
      const customText = customInputs[questionIndex];

      if (customText && customText.trim()) {
        // Save the custom answer
        onSurveyAnswerClick(
          questionIndex,
          null,
          true,
          customText.trim()
        )({ preventDefault: () => {} });

        // Clear the input
        setCustomInputs((prev) => {
          const updated = { ...prev };
          delete updated[questionIndex];
          return updated;
        });

        // Close the dropdown
        const dropdownId = isMobile
          ? `dropdownMenuButtonMobile${questionIndex}`
          : `dropdownMenuButton${questionIndex}`;
        const dropdownElement = document.getElementById(dropdownId);
        if (dropdownElement) {
          const bsDropdown =
            window.bootstrap?.Dropdown?.getInstance(dropdownElement);
          if (bsDropdown) {
            bsDropdown.hide();
          }
        }
      }
    };

  // Check parent's state for validation
  const checkAllAnswersSelected = () => {
    return survey_questions.every((question) => {
      // Check if either predefined answer is selected OR custom answer is filled
      const hasPredefinedSelection = question.survey_answers.find(
        (answer) => answer.is_selected
      );
      const hasCustomSelection =
        question.is_custom_selected &&
        question.custom_answer &&
        question.custom_answer.trim().length > 0;

      return hasPredefinedSelection !== undefined || hasCustomSelection;
    });
  };

  // Submit - no customAnswers parameter needed!
  const handleSubmit = (e) => {
    if (!checkAllAnswersSelected()) {
      setShowErrors(true);
      showToast({
        type: "error",
        message: t("error.please_select_required_boxes"),
      });
      return;
    }
    setShowErrors(false);

    // Just call onSubmit - everything is in parent's survey_questions state!
    onSubmit(e);
  };

  // Check parent's state for button styling
  const getButtonStyle = (questionIndex, isMobile = false) => {
    const question = survey_questions[questionIndex];
    const hasPredefinedSelection = question.survey_answers.find(
      (a) => a.is_selected
    );
    const hasCustomSelection =
      question.is_custom_selected && question.custom_answer;
    const hasAnySelection = hasPredefinedSelection || hasCustomSelection;

    const baseStyle = {
      fontSize: "14px",
      padding: isMobile ? "0.75rem 1rem" : "0.375rem 0.75rem",
      borderRadius: isMobile ? "25px" : "20px",
      minHeight: "50px",
      height: "auto",
      textAlign: "left",
      lineHeight: "1.5",
    };

    if (showErrors && !hasAnySelection) {
      return {
        ...baseStyle,
        borderColor: "#dc3545",
        borderWidth: "2px",
        color: "#dc3545",
      };
    }

    return baseStyle;
  };

  // Read from parent's state for display
  const getDisplayText = (questionIndex) => {
    const question = survey_questions[questionIndex];

    // Check if any answer is selected (from parent's state)
    const selectedAnswer = question.survey_answers.find((a) => a.is_selected);
    if (selectedAnswer) {
      return `${getShortLabel(questionIndex)}: ${selectedAnswer.name}`;
    }

    // If actively entering custom answer (not yet saved)
    if (question.is_custom_selected && question.custom_answer) {
      return `${getShortLabel(questionIndex)}: ${question.custom_answer}`;
    }

    // Nothing selected yet
    return question.name;
  };

  const getButtonText = () => {
    if (loading) {
      return (
        <span>
          {t("loading")} <i className="fa-solid fa-spinner fa-spin"></i>
        </span>
      );
    }
    return hasGeneratedContent ? t("recreate_chapter") : t("create_chapter");
  };

  // Render dropdown with custom input
  const renderDropdownMenu = (question, questionIndex, isMobile = false) => {
    const menuId = isMobile
      ? `dropdownMenuButtonMobile${questionIndex}`
      : `dropdownMenuButton${questionIndex}`;
    const prefix = isMobile ? "mobile_" : "";

    return (
      <ul
        className={`dropdown-menu ${isMobile ? "w-80" : ""}`}
        aria-labelledby={menuId}
        style={{
          minWidth: isMobile ? "auto" : "280px",
          maxWidth: isMobile ? "80vw" : "450px",
          zIndex: 9999,
          position: "absolute",
          transform: "translateY(0)",
        }}
      >
        <li
          className="dropdown-header"
          style={{
            fontWeight: "bold",
            whiteSpace: "normal",
            wordWrap: "break-word",
          }}
        >
          {question.name}
        </li>
        <li>
          <hr className="dropdown-divider" />
        </li>

        {/* Predefined and custom answers */}
        {question.survey_answers.map((answer, answerIndex) => (
          <li key={`answer_${prefix}${answerIndex}`}>
            <button
              className={`dropdown-item ${
                answer.is_selected ? "selected-item" : ""
              }`}
              onClick={(e) =>
                handleAnswerSelect(questionIndex, answerIndex, isMobile)(e)
              }
              style={{
                fontSize: "14px",
                padding: isMobile ? "0.75rem 1rem" : "0.5rem 1rem",
                backgroundColor: answer.is_selected ? "rgb(196, 122, 38)" : "",
                color: answer.is_selected ? "#fff" : "#000",
              }}
            >
              <i
                className={`fa-regular ${
                  answer.is_selected ? "fa-circle-check" : "fa-circle"
                } me-2`}
              ></i>
              {answer.name}
              {answer.is_custom && (
                <span
                  style={{
                    marginLeft: "8px",
                    fontSize: "11px",
                    opacity: 0.8,
                    fontStyle: "italic",
                  }}
                >
                  ({t("custom")})
                </span>
              )}
            </button>
          </li>
        ))}

        {/* Other option divider */}
        <li>
          <hr className="dropdown-divider" />
        </li>

        {/* Other option - clickable to enable custom input */}
        <li>
          <button
            className={`dropdown-item ${
              question.is_custom_selected ? "selected-item" : ""
            }`}
            onClick={(e) => {
              e.preventDefault();
              // When clicking "Other", activate custom mode with empty string
              onSurveyAnswerClick(
                questionIndex,
                null,
                true,
                ""
              )({ preventDefault: () => {} });
              // Focus the input after a short delay
              setTimeout(() => {
                const input = document.querySelector(
                  `#${menuId} input[type="text"]`
                );
                if (input) input.focus();
              }, 100);
            }}
            style={{
              fontSize: "14px",
              padding: isMobile ? "0.75rem 1rem" : "0.5rem 1rem",
              backgroundColor: question.is_custom_selected
                ? "rgb(196, 122, 38)"
                : "",
              color: question.is_custom_selected ? "#fff" : "#000",
            }}
          >
            <i
              className={`fa-regular ${
                question.is_custom_selected ? "fa-circle-check" : "fa-circle"
              } me-2`}
            ></i>
            {questionIndex === 0 ? t("other_style") : t("other")}:
          </button>
        </li>

        {/* Custom text input - only shown when "Other" is selected */}
        {question.is_custom_selected && (
          <li className="px-3 py-2">
            <div className="d-flex align-items-start gap-2">
              <textarea
                className={`form-control ${
                  showErrors && !question.custom_answer?.trim()
                    ? "is-invalid"
                    : ""
                }`}
                placeholder={
                  questionIndex === 0
                    ? t("enter_custom_style")
                    : t("enter_custom_answer")
                }
                value={customInputs[questionIndex] || ""}
                onChange={handleCustomInputChange(questionIndex)}
                onClick={(e) => e.stopPropagation()}
                onFocus={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmitCustomAnswer(questionIndex, isMobile)(e);
                  }
                }}
                style={{
                  fontSize: "14px",
                  backgroundColor: "rgb(252, 240, 220)",
                  border: "2px solid rgb(196, 122, 38)",
                  flex: 1,
                  minHeight: "60px",
                  resize: "none",
                  padding: "8px 12px",
                }}
                rows={2}
                autoFocus
              />
              <button
                type="button"
                className="btn survey-submit-btn btn-sm"
                onClick={handleSubmitCustomAnswer(questionIndex, isMobile)}
                disabled={!customInputs[questionIndex]?.trim()}
                style={{
                  minWidth: "40px",
                  height: "40px",
                  padding: "0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginTop: "2px",
                }}
              >
                <i className="fa-solid fa-check"></i>
              </button>
            </div>
            {showErrors && !question.custom_answer?.trim() && (
              <div className="invalid-feedback d-block">
                {t("error.custom_answer_required")}
              </div>
            )}
          </li>
        )}
      </ul>
    );
  };

  return (
    <div className="container-fluid px-3">
      {/* Desktop layout */}
      <div className="d-none d-md-flex align-items-center flex-wrap gap-2 justify-content-center">
        {survey_questions.map((question, questionIndex) => (
          <div
            key={`survey_dropdown_${questionIndex}`}
            className="dropup"
            style={{ position: "static" }}
          >
            <button
              className={`btn btn-outline-secondary dropdown-toggle survey-dropdown-button ${
                showErrors && !checkAllAnswersSelected() ? "dropdown-error" : ""
              }`}
              type="button"
              id={`dropdownMenuButton${questionIndex}`}
              data-bs-toggle="dropdown"
              data-bs-auto-close="outside"
              aria-expanded="false"
              style={getButtonStyle(questionIndex, false)}
            >
              {getDisplayText(questionIndex)}
            </button>
            {renderDropdownMenu(question, questionIndex, false)}
          </div>
        ))}

        <button
          className="survey-create-button"
          disabled={loading}
          onClick={handleSubmit}
        >
          {getButtonText()}
        </button>
      </div>

      {/* Mobile layout */}
      <div className="d-md-none d-flex flex-column align-items-center gap-3 w-80">
        {survey_questions.map((question, questionIndex) => (
          <div
            key={`survey_dropdown_mobile_${questionIndex}`}
            className="dropup w-80"
            style={{
              position: "static",
              maxWidth: "80vw",
            }}
          >
            <button
              className={`btn btn-outline-secondary dropdown-toggle survey-dropdown-button-mobile ${
                showErrors && !checkAllAnswersSelected() ? "dropdown-error" : ""
              }`}
              type="button"
              id={`dropdownMenuButtonMobile${questionIndex}`}
              data-bs-toggle="dropdown"
              data-bs-auto-close="outside"
              aria-expanded="false"
              style={getButtonStyle(questionIndex, true)}
            >
              {getDisplayText(questionIndex)}
            </button>
            {renderDropdownMenu(question, questionIndex, true)}
          </div>
        ))}

        <button
          className="survey-create-button survey-create-button-mobile"
          disabled={loading}
          onClick={handleSubmit}
        >
          {getButtonText()}
        </button>
      </div>
    </div>
  );
};

export default InlineSurveyDropdowns;
